import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  message,
  Alert,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import axios from "axios";
import { useSelector } from "react-redux";
import config from "../../config";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const LeaveRequestPage = ({ visible, onCancel, onSuccess, balance }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [leaveDays, setLeaveDays] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const account = useSelector((state) => state.account.user);
  const userId = account.id;
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  // Calculate working days (Mon-Fri) between two dates
  const calculateLeaveDays = (start, end) => {
    if (!start || !end) return 0;
    let days = 0;
    let current = start.clone();
    while (current <= end) {
      const day = current.day();
      if (day !== 0 && day !== 6) days++; // exclude Sunday (0) and Saturday (6)
      current = current.add(1, "day");
    }
    return days;
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
      const days = calculateLeaveDays(start, end);
      setLeaveDays(days);
      form.setFieldsValue({ leave_days: days });
    } else {
      setStartDate(null);
      setEndDate(null);
      setLeaveDays(0);
      form.setFieldsValue({ leave_days: 0 });
    }
  };

  const onFinish = async (values) => {
    if (leaveDays > balance) {
      message.error(
        `Insufficient leave balance. You have ${balance} days left.`,
      );
      return;
    }
    setLoading(true);
    try {
      const payload = {
        userId,
        startDate: values.dates[0].format("YYYY-MM-DD"),
        endDate: values.dates[1].format("YYYY-MM-DD"),
        type: values.type,
        reason: values.reason,
      };
      const { data } = await api.post(`/leave/request`, payload);
      message.success(data.message || "Leave request submitted successfully.");
      form.resetFields();
      setStartDate(null);
      setEndDate(null);
      setLeaveDays(0);
      onSuccess();
      onCancel();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to submit request.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Request Leave"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="type"
          label="Leave Type"
          rules={[{ required: true, message: "Please select leave type" }]}
          initialValue="vacation"
        >
          <Select>
            <Option value="vacation">Vacation</Option>
            <Option value="sick">Sick Leave</Option>
            <Option value="maternity">Maternity Leave</Option>
            <Option value="paternity">Paternity Leave</Option>
            <Option value="other">Other</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="dates"
          label="Select Dates"
          rules={[
            { required: true, message: "Please select start and end dates" },
          ]}
        >
          <DatePicker.RangePicker
            onChange={handleDateChange}
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        </Form.Item>

        <Form.Item label="Leave Days (calculated)">
          <Input value={leaveDays} disabled />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ required: true, message: "Please provide a reason" }]}
        >
          <TextArea
            rows={4}
            placeholder="Please provide details for your leave request..."
          />
        </Form.Item>

        {leaveDays > balance && (
          <Alert
            message={`Insufficient balance: You have ${balance} days, requested ${leaveDays} days`}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={leaveDays > balance}
            >
              Submit Request
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LeaveRequestPage;
